/**
 * 이 패키지가 구현을 갖는 derive 들.
 *
 * 호스트는 이걸 자기 레지스트리에 펼쳐 넣고, I/O 가 필요한 loader 만 따로 채운다.
 * 그래야 프리뷰와 프로덕션이 같은 계산을 한다.
 */
import { MOVING_ITEM_REFS } from "../data/movingItems";
import {
  buildDeparturePrefill,
  type AssignedDepartureLocation,
} from "./departurePrefill";
import type { ItemSpec } from "./itemSpec";
import {
  buildMovingItemPickerSections,
  type MovingItemPickerFlowItem,
} from "./movingItemSections";
import {
  arrivalAddressToFlowValues,
  movingMethodToFlowValues,
  type MovingMethodValue,
  type StructuredAddress,
} from "./progressPrefill";
import { ADDITIONAL_SPACE_REFS } from "../documents/additionalSpaces";
import { HOUSE_STRUCTURE_REFS } from "../documents/houseStructureDone";
import { ROOM_PICK_REFS } from "../documents/pickRoom";
import {
  pickerDoneInstances,
  pickerSections,
  type PickEntry,
} from "./pickEntries";
import { houseStructureRooms } from "./houseStructureRooms";
import { MOVING_TYPE_REFS } from "../documents/movingTypes";
import {
  MOVING_TYPE_CODE_FALLBACK_LABELS,
  MOVING_TYPE_EMOJI,
  type MovingTypeSpecificationResponse,
} from "./movingTypeSpec";
import { movingTypeSpecToFlowDocument } from "./movingTypeSpecToFlowDocument";

/**
 * 스펙 하나를 카드 하나로.
 *
 * 아이콘은 `iconDownloadUrl` 을 그대로 쓴다 — 짐 카드에 실제 아이콘이 뜨려면 이 필드가
 * API 응답에 있어야 하고, 없으면 카드가 글자만 남는다.
 *
 * `description`(서비스 범위 문구)과 `enabledModeIds`(모드 게이팅)는 여기서 채우지
 * 않는다. 스펙의 제약 조건 해석이 필요한데 그 규칙이 아직 앱에 있다. 호스트가
 * `enrich` 로 덧붙인다.
 */
function specToItem(spec: ItemSpec): MovingItemPickerFlowItem {
  return {
    id: spec.id,
    title: spec.name,
    ...(spec.iconDownloadUrl ? { icon: spec.iconDownloadUrl } : {}),
    // sub-flow id → 호스트의 flowFetcher 가 spec 단건을 받아 상세 플로우를 만든다.
    flow: spec.id,
  };
}

export interface MovingItemSectionArgs {
  specs?: readonly ItemSpec[];
  roomType?: string;
}

/** 호스트가 카드에 자기 도메인 정보를 덧붙일 때 쓰는 훅. */
export type ItemEnricher = (
  item: MovingItemPickerFlowItem,
  spec: ItemSpec,
) => MovingItemPickerFlowItem;

export function createCounselDerives(options: { enrich?: ItemEnricher } = {}) {
  const { enrich } = options;
  return {
    /**
     * 주문 배정 출발지 → 상담 시작 화면의 주소·건물명 기본값.
     *
     * 순수 함수라 여기 둔다. 프리뷰와 프로덕션이 다른 구현을 쓰면 "건물명이 채워지는가"
     * 를 프리뷰에서 확인하는 의미가 없어진다.
     */
    "departure.prefillFromOrder": (args: {
      location?: AssignedDepartureLocation | null;
    }) => buildDeparturePrefill(args.location),

    /** 저장된 도착지 주소 → 질문 id 별 초기값. */
    "arrival.prefillFromCounsel": (args: {
      address?: StructuredAddress | null;
    }) => arrivalAddressToFlowValues(args.address),

    /** 저장된 운반방식 → 질문 id 별 초기값 (분기 답 포함). */
    "transport.prefillFromCounsel": (args: {
      movingMethod?: MovingMethodValue | null;
      hasElevator?: boolean | null;
    }) => movingMethodToFlowValues(args.movingMethod, args.hasElevator),

    /**
     * 이사 유형 스펙 → 카드 한 줄짜리 섹션.
     *
     * 서브플로우는 카드에 인라인으로 붙인다(프로덕션과 같음). 유형마다 묻는 항목이
     * 스펙에서 나오므로 별도 조회 없이 카드가 자기 질문을 들고 다닌다.
     */
    [MOVING_TYPE_REFS.pickerSections]: (args: {
      specs?: readonly MovingTypeSpecificationResponse[];
      movingDate?: string | null;
    }) => {
      const specs = args.specs ?? [];
      if (specs.length === 0) return [];
      return [
        {
          id: "all",
          title: "이사 유형",
          items: specs.map((spec) => ({
            id: spec.code,
            title:
              spec.name?.trim() ||
              MOVING_TYPE_CODE_FALLBACK_LABELS[spec.code] ||
              spec.code,
            // 아이콘이 없으면 이모지로 떨어진다 — 카드가 글자만 남지 않게.
            icon: spec.iconDownloadUrl ?? MOVING_TYPE_EMOJI[spec.code],
            flow: movingTypeSpecToFlowDocument(spec, {
              movingDate: args.movingDate ?? null,
            }),
          })),
        },
      ];
    },

    /** 집 구조 transform 출력 → 이름 붙은 방 목록. */
    [HOUSE_STRUCTURE_REFS.rooms]: (args: { raw?: unknown }) =>
      houseStructureRooms(args.raw),

    /*
     * 방·추가공간 선택 화면의 섹션과 완료 배지.
     *
     * 목록을 만드는 것(상담 스냅샷 해석)은 호스트 몫이고, 그 목록을 어떻게 나누고
     * 어떤 이모지를 붙일지는 도메인 규칙이라 여기 있다.
     */
    [ROOM_PICK_REFS.sections]: (args: { entries?: readonly PickEntry[] }) =>
      pickerSections(args.entries ?? [], "room"),
    [ROOM_PICK_REFS.doneInstances]: (args: { entries?: readonly PickEntry[] }) =>
      pickerDoneInstances(args.entries ?? []),
    [ADDITIONAL_SPACE_REFS.sections]: (args: {
      entries?: readonly PickEntry[];
    }) => pickerSections(args.entries ?? [], "additional"),
    [ADDITIONAL_SPACE_REFS.doneInstances]: (args: {
      entries?: readonly PickEntry[];
    }) => pickerDoneInstances(args.entries ?? []),

    /**
     * 지금 짐을 기록하는 자리.
     *
     * 두 경로가 있다. 사용자가 방·추가공간 카드를 눌러 왔으면 그 화면이 이미 정해서
     * 넘긴다. 현관처럼 규칙이 지목한 경우에는 스테이지 판정의 `target` 밖에 없다.
     * 넘어온 값이 있으면 그게 이긴다 — 고른 방이 규칙의 지목으로 덮이면 안 된다.
     */
    "counsel.recordTarget": (args: {
      target?: { id?: string; label?: string; type?: string } | null;
      targetRoomId?: string | null;
      targetSessionId?: string | null;
      roomLabel?: string | null;
      roomType?: string | null;
    }) => {
      const t = args.target ?? null;
      const out: Record<string, unknown> = {};
      const roomId = args.targetRoomId ?? t?.id;
      const label = args.roomLabel ?? t?.label;
      const type = args.roomType ?? t?.type;
      if (roomId) out.targetRoomId = roomId;
      if (args.targetSessionId) out.targetSessionId = args.targetSessionId;
      if (label) out.roomLabel = label;
      if (type) out.roomType = type;
      return out;
    },

    [MOVING_ITEM_REFS.pickerSections]: (args: MovingItemSectionArgs) =>
      buildMovingItemPickerSections({
        specs: [...(args.specs ?? [])],
        ...(args.roomType ? { currentRoomType: args.roomType } : {}),
        toItem: (spec) => {
          const item = specToItem(spec);
          return enrich ? enrich(item, spec) : item;
        },
      }),
  };
}
